import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type {
    Faq,
    ProjectSummary,
    Service,
    ServiceSummary
} from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaLink } from '@/components/generals/cta-link'
import { HeroTitle } from '@/components/generals/hero-title'
import { RollText } from '@/components/generals/roll-text'
import { SectionLabel } from '@/components/generals/section-label'
import ProcessSection from '@/components/sections/process-section'
import ProjectGrid from '@/components/sections/project-grid'
import FaqSection from '@/components/sections/faq-section'
import FinalCta from '@/components/sections/final-cta'

import {
    REVALIDATE_SECONDS,
    getFaqs,
    getProjectsForService,
    getService,
    getServices,
    getSlugs
} from '@/lib/content'

interface ServicePageProps {
    service: Service
    number: number
    projects: ProjectSummary[]
    others: ServiceSummary[]
    faqs: Faq[]
}

const ServicePage: FC<ServicePageProps> = ({ service, number, projects, others, faqs }): ReactNode => {
    /* `others` is every service except this one, in order, so the one after this sits at index number - 1. */
    const upcoming = others[number - 1] ?? others[0] ?? null

    return (
        <Layout>
            <Seo title={service.title} description={service.summary} ogImage={service.image ?? undefined} />

            <section id='service-hero' aria-labelledby='service-title' data-nav-theme='light' className='relative overflow-hidden bg-cream text-charcoal'>
                <div className='mx-auto max-w-[1440px] px-5 pb-20 pt-32 md:px-10 md:pb-24 md:pt-40'>
                    <div className='relative flex flex-wrap items-center justify-between gap-x-6 gap-y-4 pb-5'>
                        <span aria-hidden='true' className='reveal-rule absolute inset-x-0 bottom-0 h-px bg-charcoal/20' style={revealDelay(100)} />
                        <Link href='/services' className='reveal-fade group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/80 transition-colors hover:text-charcoal'>
                            <ArrowLeft aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-x-1' />
                            All services
                        </Link>
                        {upcoming && (
                            <Link
                                href={`/services/${upcoming.slug}`}
                                className='reveal-fade group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/80 transition-colors hover:text-charcoal'
                                style={revealDelay(150)}
                            >
                                <span className='text-charcoal/75'>Next</span>
                                <RollText>{upcoming.shortTitle}</RollText>
                                <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                            </Link>
                        )}
                    </div>

                    <div className='mt-10 grid gap-14 md:mt-14 lg:grid-cols-12 lg:items-center lg:gap-10'>
                        <div className='lg:col-span-6 xl:col-span-7'>
                            <SectionLabel className='reveal-fade'>Service {pad(number)} / {pad(others.length + 1)}</SectionLabel>
                            <HeroTitle id='service-title' className='mt-6 type-hero' delay={200} lines={[service.title]} />
                            <p className='reveal-fade mt-6 max-w-xl text-lg leading-relaxed text-charcoal/85 md:mt-8' style={revealDelay(380)}>{service.summary}</p>

                            {service.deliverables.length > 0 && (
                                <ul aria-label='What this includes' className='reveal-fade mt-8 flex flex-wrap gap-2' style={revealDelay(460)}>
                                    {service.deliverables.slice(0, 4).map((deliverable) => (
                                        <li key={deliverable} className='rounded-sm border border-charcoal/25 px-3 py-1.5 text-[13px] font-medium'>
                                            {deliverable}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div className='reveal-fade mt-9 flex flex-wrap items-center gap-x-8 gap-y-5' style={revealDelay(540)}>
                                <CtaLink href='/contact'>Start a project</CtaLink>
                                <CtaLink href='#deliverables' variant='text'>See deliverables</CtaLink>
                            </div>
                        </div>

                        <div className='relative lg:col-span-6 xl:col-span-5'>
                            <div aria-hidden='true' className='absolute -right-3 -top-3 h-2/3 w-2/3 md:-right-5 md:-top-5' data-parallax='6'>
                                <div className='reveal-clip size-full bg-coral' style={revealDelay(260)} />
                            </div>
                            <div className='reveal-clip relative aspect-[4/3] overflow-hidden bg-stone lg:aspect-square' style={revealDelay(340)}>
                                {service.image && (
                                    <Image src={service.image} alt={service.imageAlt} fill priority sizes='(min-width: 1024px) 40vw, 100vw' className='object-cover' />
                                )}
                                <span aria-hidden='true' className='absolute bottom-0 left-0 bg-cream px-3 py-2 font-heading font-wide text-2xl font-black leading-none tracking-[-0.04em] md:text-3xl'>
                                    {pad(number)}
                                </span>
                            </div>
                            <div aria-hidden='true' className='reveal-fade absolute -bottom-4 -right-4 size-14 border border-charcoal md:size-16' style={revealDelay(700)} />
                        </div>
                    </div>
                </div>
            </section>

            {(service.description || service.approach) && (
                <section aria-labelledby='overview-title' data-nav-theme='dark' className='bg-charcoal text-cream'>
                    <div className='mx-auto grid max-w-[1440px] gap-14 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:gap-10'>
                        <div data-reveal className='lg:col-span-7'>
                            <SectionLabel tone='dark'>Overview</SectionLabel>
                            <h2 id='overview-title' className='sr-only'>Overview</h2>
                            <p className='mt-8 font-heading text-[clamp(1.4rem,2.3vw,2.15rem)] font-semibold leading-[1.26] tracking-[-0.025em]'>
                                {service.description}
                            </p>
                        </div>
                        {service.approach && (
                            <div data-reveal className='border-t border-cream/25 pt-6 lg:col-span-4 lg:col-start-9 lg:self-end'>
                                <h3 className='font-mono text-[11px] uppercase tracking-[0.18em] text-coral'>Our approach</h3>
                                <p className='mt-4 text-[16px] leading-relaxed text-cream/80'>{service.approach}</p>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {service.challenges.length > 0 && (
                <section aria-labelledby='challenges-title' data-nav-theme='light' className='bg-stone text-charcoal'>
                    <div className='mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:gap-10'>
                        <div data-reveal className='lg:col-span-4'>
                            <SectionLabel>Business challenges</SectionLabel>
                            <h2 id='challenges-title' className='mt-6 font-heading type-section'>
                                Sound familiar?
                            </h2>
                        </div>
                        <ul className='grid border-t border-charcoal sm:grid-cols-2 lg:col-span-8'>
                            {service.challenges.map((challenge, index) => (
                                <li key={challenge} data-reveal className='flex gap-5 border-b border-charcoal/30 py-7 sm:px-6 sm:[&:nth-child(odd)]:border-r sm:[&:nth-child(odd)]:pl-0'>
                                    <span className='font-mono text-xs text-charcoal/75'>{pad(index + 1)}</span>
                                    <p className='font-heading text-lg font-bold leading-snug tracking-[-0.015em] md:text-xl'>{challenge}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {service.deliverables.length > 0 && (
                <section id='deliverables' aria-labelledby='deliverables-title' data-nav-theme='light' className='scroll-mt-20 bg-cream text-charcoal'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <div data-reveal>
                            <SectionLabel>What you get</SectionLabel>
                            <h2 id='deliverables-title' className='mt-6 font-heading type-section'>
                                Deliverables.
                            </h2>
                        </div>
                        <ol className={cn('mt-14 grid border-l border-t border-charcoal/25 sm:grid-cols-2', service.deliverables.length >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4')}>
                            {service.deliverables.map((deliverable, index) => (
                                <li key={deliverable} data-reveal className='group flex min-h-44 flex-col justify-between gap-8 border-b border-r border-charcoal/25 p-6 transition-colors duration-300 hover:bg-coral'>
                                    <span className='font-heading font-wide text-3xl font-black tracking-[-0.04em]'>{pad(index + 1)}</span>
                                    <span className='font-heading text-lg font-bold leading-snug tracking-[-0.015em]'>{deliverable}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>
            )}

            <ProcessSection label='How we work' />

            {projects.length > 0 && (
                <section aria-labelledby='service-projects-title' data-nav-theme='dark' className='bg-charcoal text-cream'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <div data-reveal>
                            <SectionLabel tone='dark'>Relevant projects</SectionLabel>
                            <h2 id='service-projects-title' className='mt-6 font-heading type-section'>
                                {service.shortTitle} <span className='text-coral'>in practice.</span>
                            </h2>
                        </div>
                        <ProjectGrid projects={projects.slice(0, 2)} className='mt-14' />
                    </div>
                </section>
            )}

            {others.length > 0 && (
                <section aria-labelledby='other-services-title' data-nav-theme='light' className='bg-stone text-charcoal'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-24'>
                        <SectionLabel>Other services</SectionLabel>
                        <h2 id='other-services-title' className='sr-only'>Other services</h2>
                        <ul className='mt-10 border-t border-charcoal'>
                            {others.map((other) => (
                                <li key={other.id} className='border-b border-charcoal/25'>
                                    <Link href={`/services/${other.slug}`} className='group flex items-center justify-between gap-6 py-5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral'>
                                        <span className='font-heading font-wide text-[clamp(1.15rem,2vw,1.75rem)] font-black uppercase tracking-[-0.02em] transition-transform duration-300 group-hover:translate-x-2'>
                                            {other.title}
                                        </span>
                                        <ArrowUpRight aria-hidden='true' className='size-6 shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        <Link href='/services' className='mt-8 inline-flex items-center gap-2 border-b border-charcoal pb-1 text-[13px] font-semibold uppercase tracking-[0.08em]'>
                            All services
                            <ArrowRight aria-hidden='true' className='size-4' />
                        </Link>
                    </div>
                </section>
            )}

            <FaqSection faqs={faqs} label='FAQ' />
            <FinalCta />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getSlugs('services')

    return {
        paths: slugs.map((slug) => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<ServicePageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const service = slug ? await getService(slug) : null

    if (!service) return { notFound: true, revalidate: REVALIDATE_SECONDS }

    const [services, projects, faqs] = await Promise.all([getServices(), getProjectsForService(slug), getFaqs()])
    const index = services.findIndex((item) => item.slug === slug)

    return {
        props: {
            service,
            number: index >= 0 ? index + 1 : 1,
            projects,
            others: services
                .filter((item) => item.slug !== slug)
                .map(({ id, slug: otherSlug, title, shortTitle, summary, image, imageAlt }) => ({ id, slug: otherSlug, title, shortTitle, summary, image, imageAlt })),
            faqs
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default ServicePage
